import { useEffect, useMemo, useRef, type ReactNode, type MutableRefObject } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { MicOff, Pin, PinOff, VideoOff } from "lucide-react";

export interface GridParticipantTile {
  id: string;
  stream: MediaStream | null;
  name?: string;
  isLocal?: boolean;
  isVideoEnabled?: boolean;
  isAudioEnabled?: boolean;
}

interface ParticipantVideoGridProps {
  participants: GridParticipantTile[];
  activeSpeaker?: string | null;
  spotlightedParticipant?: string | null;
  onSpotlightChange?: (participantId: string | null) => void;
  renderLocalActions?: (participant: GridParticipantTile) => ReactNode;
  renderSpeakingTime?: (participantId: string) => ReactNode;
  localVideoRef?: MutableRefObject<HTMLVideoElement | null>;
}

export function ParticipantVideoGrid({
  participants,
  activeSpeaker,
  spotlightedParticipant,
  onSpotlightChange,
  renderLocalActions,
  renderSpeakingTime,
  localVideoRef,
}: ParticipantVideoGridProps) {
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

  useEffect(() => {
    participants.forEach((participant) => {
      if (!participant.stream) return;
      const videoElement = videoRefs.current.get(participant.id);
      if (videoElement && videoElement.srcObject !== participant.stream) {
        videoElement.srcObject = participant.stream;
      }
    });
  }, [participants]);

  const orderedParticipants = useMemo(() => {
    if (!spotlightedParticipant) return participants;
    const spotlightTile = participants.find((p) => p.id === spotlightedParticipant);
    const others = participants.filter((p) => p.id !== spotlightedParticipant);
    return spotlightTile ? [spotlightTile, ...others] : participants;
  }, [participants, spotlightedParticipant]);

  if (participants.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-muted/40 rounded-lg">
        <p className="text-muted-foreground">Waiting for participants…</p>
      </div>
    );
  }

  if (spotlightedParticipant && orderedParticipants[0].id === spotlightedParticipant) {
    const spotlightTile = orderedParticipants[0];
    const secondaryTiles = orderedParticipants.slice(1);
    const isActiveSpeaker = activeSpeaker === spotlightTile.id;

    return (
      <div className="h-full flex flex-col gap-3 lg:gap-4">
        <Card
          className={`relative flex-1 min-h-[220px] md:min-h-[320px] lg:min-h-[420px] xl:min-h-[520px] overflow-hidden bg-muted transition-all ${
            isActiveSpeaker ? "ring-4 ring-primary shadow-lg shadow-primary/40" : ""
          }`}
        >
          {spotlightTile.isVideoEnabled !== false && spotlightTile.stream ? (
            <video
              ref={(el) => {
                if (!el) return;
                if (spotlightTile.isLocal && localVideoRef) {
                  localVideoRef.current = el;
                }
                videoRefs.current.set(spotlightTile.id, el);
                if (el.srcObject !== spotlightTile.stream) {
                  el.srcObject = spotlightTile.stream;
                }
              }}
              className="w-full h-full object-contain"
              autoPlay
              playsInline
              muted={spotlightTile.isLocal}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-muted/60">
              <div className="text-center space-y-3">
                <div className="w-24 h-24 md:w-32 md:h-32 bg-primary/20 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-4xl md:text-5xl font-semibold text-primary">
                    {spotlightTile.name?.[0]?.toUpperCase() || "U"}
                  </span>
                </div>
                <p className="text-lg text-muted-foreground">{spotlightTile.name || "Participant"}</p>
              </div>
            </div>
          )}

          {renderLocalActions && spotlightTile.isLocal && (
            <div className="absolute top-3 right-16 flex gap-2">
              {renderLocalActions(spotlightTile)}
            </div>
          )}

          <div className="absolute top-3 right-3 flex gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => onSpotlightChange?.(null)}
                  className="rounded-full h-9 w-9 bg-background/80 hover:bg-background"
                >
                  <PinOff className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Unpin participant</TooltipContent>
            </Tooltip>
          </div>

          <div
            className={`absolute bottom-3 left-3 px-3 py-1.5 rounded-md text-sm flex items-center space-x-2 ${
              isActiveSpeaker ? "bg-primary text-primary-foreground" : "bg-black/70 text-white"
            }`}
          >
            <Pin className="h-3.5 w-3.5" />
            <span>{spotlightTile.name || "Participant"}</span>
            {spotlightTile.isAudioEnabled === false && <MicOff className="h-3.5 w-3.5" />}
          </div>

          {renderSpeakingTime && (
            <div className="absolute bottom-3 right-3">
              {renderSpeakingTime(spotlightTile.id)}
            </div>
          )}
        </Card>

        {secondaryTiles.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3 lg:gap-4">
            {secondaryTiles.map((tile) => {
              const isActive = activeSpeaker === tile.id;
              const hasVideo = tile.isVideoEnabled !== false && Boolean(tile.stream);

              return (
                <Card
                  key={tile.id}
                  className={`group relative min-h-[140px] sm:min-h-[180px] bg-muted overflow-hidden cursor-pointer transition-all ${
                    isActive ? "ring-2 ring-primary shadow-lg shadow-primary/30" : ""
                  }`}
                  onClick={() => onSpotlightChange?.(tile.id)}
                >
                  {hasVideo ? (
                    <video
                      ref={(el) => {
                        if (!el) return;
                        if (tile.isLocal && localVideoRef) {
                          localVideoRef.current = el;
                        }
                        videoRefs.current.set(tile.id, el);
                        if (tile.stream && el.srcObject !== tile.stream) {
                          el.srcObject = tile.stream;
                        }
                      }}
                      className="w-full h-full object-contain"
                      autoPlay
                      playsInline
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-muted/60">
                      <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                        <span className="text-lg font-semibold text-primary">{tile.name?.[0]?.toUpperCase() || "U"}</span>
                      </div>
                    </div>
                  )}

                  <div
                    className={`absolute bottom-2 left-2 px-2 py-1 rounded text-xs flex items-center space-x-1 ${
                      isActive ? "bg-primary text-primary-foreground" : "bg-black/70 text-white"
                    }`}
                  >
                    <span className="truncate max-w-[120px]">{tile.name || "Participant"}</span>
                    {tile.isAudioEnabled === false && <MicOff className="h-3 w-3" />}
                  </div>

                  {renderSpeakingTime && (
                    <div className="absolute bottom-2 right-2">
                      {renderSpeakingTime(tile.id)}
                    </div>
                  )}

                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="secondary"
                          size="icon"
                          className="rounded-full h-7 w-7 bg-background/80 hover:bg-background"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSpotlightChange?.(tile.id);
                          }}
                        >
                          <Pin className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Pin participant</TooltipContent>
                    </Tooltip>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const gridClass =
    participants.length === 1
      ? "grid-cols-1"
      : participants.length === 2
      ? "grid-cols-1 md:grid-cols-2"
      : participants.length <= 4
      ? "grid-cols-2"
      : participants.length <= 6
      ? "grid-cols-2 md:grid-cols-3"
      : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4";

  return (
    <div className={`grid ${gridClass} gap-2 sm:gap-3 md:gap-4 w-full h-full auto-rows-fr`}>
      {orderedParticipants.map((tile) => {
        const hasVideo = tile.isVideoEnabled !== false && Boolean(tile.stream);
        const isActive = activeSpeaker === tile.id;

        return (
          <Card
            key={tile.id}
            className={`group relative bg-muted overflow-hidden min-h-[150px] sm:min-h-[180px] md:min-h-[220px] lg:min-h-[260px] transition-all duration-300 cursor-pointer hover:ring-2 hover:ring-primary/40 ${
              isActive ? "ring-4 ring-primary shadow-lg shadow-primary/40" : ""
            }`}
            onClick={() => onSpotlightChange?.(tile.id)}
          >
            {hasVideo ? (
              <video
                ref={(el) => {
                  if (!el) return;
                  if (tile.isLocal && localVideoRef) {
                    localVideoRef.current = el;
                  }
                  videoRefs.current.set(tile.id, el);
                  if (tile.stream && el.srcObject !== tile.stream) {
                    el.srcObject = tile.stream;
                  }
                }}
                className="w-full h-full object-contain"
                autoPlay
                playsInline
                muted={tile.isLocal}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted/60">
                <div className="text-center">
                  <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl font-semibold text-primary">{tile.name?.[0]?.toUpperCase() || "U"}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{tile.name || "Participant"}</p>
                </div>
              </div>
            )}

            <div
              className={`absolute bottom-2 left-2 px-2 py-1 rounded text-xs flex items-center space-x-1 transition-all ${
                isActive ? "bg-primary text-primary-foreground" : "bg-black/70 text-white"
              }`}
            >
              <span className="truncate max-w-[160px]">{tile.name || "Participant"}</span>
              {tile.isAudioEnabled === false && <MicOff className="h-3 w-3" />}
              {tile.isVideoEnabled === false && <VideoOff className="h-3 w-3" />}
            </div>

            {renderLocalActions && tile.isLocal && (
              <div className="absolute top-2 right-14 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {renderLocalActions(tile)}
              </div>
            )}

            {renderSpeakingTime && (
              <div className="absolute bottom-2 right-2">
                {renderSpeakingTime(tile.id)}
              </div>
            )}

            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="rounded-full h-8 w-8 bg-background/80 hover:bg-background"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSpotlightChange?.(tile.id);
                    }}
                  >
                    <Pin className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Pin participant</TooltipContent>
              </Tooltip>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
